import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreHorizontal, Eye, RotateCcw, ToggleLeft, ToggleRight, Plus } from 'lucide-react';
import { useDebounce, useUrlTable } from '@/hooks';
import Routes from '@/router/routes';
import {
  useOrganizations,
  useUpdateSubscription,
  useRestoreOrganization,
  useCreateOrganization,
} from '@/hooks/api/useOrganizations';
import { Organization } from '@/api/organizations';
import { OrganizationFilters } from '@/components/organization/OrganizationFilters';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { CreateOrganizationDialog } from '@/components/dialogs/CreateOrganizationDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SortableTableHead, SortOrder } from '@/components/ui/sortable-table-head';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate } from '@/lib/date';

interface TableState {
  page: number;
  limit: number;
  search: string;
  includeDeleted: string;
  subscribed: string;
  dateRange: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export default function SuperAdminOrganizations() {
  const navigate = useNavigate();
  const { state, setPage, setSearch, setFilter, setSort, resetFilters } = useUrlTable<TableState>({
    page: 1,
    limit: 25,
    search: '',
    includeDeleted: 'false',
    subscribed: 'all',
    dateRange: 'all',
    sortBy: '',
    sortOrder: 'desc',
  });

  const debouncedSearch = useDebounce(state.search, 300);

  // Build API filters
  const filters = {
    page: state.page,
    limit: state.limit,
    search: debouncedSearch || undefined,
    includeDeleted: state.includeDeleted === 'true',
    subscribed: state.subscribed === 'all' ? undefined : state.subscribed === 'true',
    dateRange: state.dateRange === 'all' ? undefined : (state.dateRange as any),
    sortBy: (state.sortBy || undefined) as 'name' | 'email' | 'createdAt' | 'memberCount' | 'workspaceCount' | undefined,
    sortOrder: state.sortOrder,
  };

  const { data, isLoading, isError } = useOrganizations(filters);
  const updateSubscription = useUpdateSubscription();
  const restoreOrganization = useRestoreOrganization();
  const createOrganization = useCreateOrganization();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant?: 'default' | 'destructive';
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  const handleRowClick = (org: Organization) => {
    navigate(`${Routes.super_admin_dashboard_single_organization.path}/${org._id}`);
  };

  const handleToggleSubscription = (org: Organization, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDialog({
      open: true,
      title: `${org.subscribed ? 'Deactivate' : 'Activate'} Subscription`,
      description: `Are you sure you want to ${org.subscribed ? 'deactivate' : 'activate'} the subscription for ${org.name}?`,
      onConfirm: () => {
        updateSubscription.mutate(
          { id: org._id, payload: { subscribed: !org.subscribed } },
          { onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })) }
        );
      },
    });
  };

  const handleRestore = (org: Organization, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDialog({
      open: true,
      title: 'Restore Organization',
      description: `Are you sure you want to restore ${org.name}?`,
      onConfirm: () => {
        restoreOrganization.mutate(org._id, {
          onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })),
        });
      },
    });
  };

  const handleSort = (sortBy: string, sortOrder: SortOrder) => {
    setSort(sortBy, sortOrder);
  };

  const handleCreateOrganization = (
    name: string,
    ownerEmail: string,
    createDefaultWorkspace: boolean
  ) => {
    createOrganization.mutate(
      { name, ownerEmail, createDefaultWorkspace },
      {
        onSuccess: (org) => {
          setCreateDialogOpen(false);
          navigate(`${Routes.super_admin_dashboard_single_organization.path}/${org._id}`);
        },
      }
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Organizations</h1>
          <p className="text-muted-foreground mt-1">Manage all organizations in the system</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Organization
        </Button>
      </div>

      <div className="bg-card rounded-lg shadow-sm border border-border p-4">
        <OrganizationFilters
          search={state.search}
          onSearchChange={(value) => setSearch(value)}
          includeDeleted={String(state.includeDeleted)}
          onIncludeDeletedChange={(value) => setFilter('includeDeleted', value)}
          subscribed={state.subscribed}
          onSubscribedChange={(value) => setFilter('subscribed', value)}
          dateRange={state.dateRange}
          onDateRangeChange={(value) => setFilter('dateRange', value)}
          onReset={resetFilters}
        />
      </div>

      <div className="bg-card rounded-lg shadow-sm border border-border">
        {isLoading ? (
          <div className="p-4">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-12 flex-1" />
                </div>
              ))}
            </div>
          </div>
        ) : isError ? (
          <div className="p-8 text-center">
            <p className="text-red-600">Failed to load organizations. Please try again.</p>
          </div>
        ) : !data || data.data.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-muted-foreground">No organizations found.</p>
            {(state.search || state.includeDeleted === 'true' || state.subscribed !== 'all' || state.dateRange !== 'all') && (
              <Button variant="link" onClick={resetFilters} className="mt-2">
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    sortKey="name"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Name
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="email"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Owner
                  </SortableTableHead>
                  <TableHead>Plan</TableHead>
                  <SortableTableHead
                    sortKey="memberCount"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Members
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="workspaceCount"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Workspaces
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="createdAt"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Created
                  </SortableTableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.data.map((org) => (
                  <TableRow
                    key={org._id}
                    onClick={() => handleRowClick(org)}
                    className="cursor-pointer"
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {org.name}
                        {org.deletedAt && (
                          <Badge variant="deleted" className="text-xs">
                            Deleted
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium text-sm">
                          {org.owner.name || org.owner.email}
                        </div>
                        {org.owner.name && (
                          <div className="text-xs text-muted-foreground">{org.owner.email}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={org.subscribed ? 'paid' : 'free'}>
                        {org.subscribed ? 'Paid' : 'Free'}
                      </Badge>
                    </TableCell>
                    <TableCell>{org.memberCount || 0}</TableCell>
                    <TableCell>{org.workspaceCount || 0}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(org.createdAt)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(org);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {!org.deletedAt && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={(e) => handleToggleSubscription(org, e)}>
                                {org.subscribed ? (
                                  <>
                                    <ToggleLeft className="h-4 w-4 mr-2" />
                                    Deactivate Subscription
                                  </>
                                ) : (
                                  <>
                                    <ToggleRight className="h-4 w-4 mr-2" />
                                    Activate Subscription
                                  </>
                                )}
                              </DropdownMenuItem>
                            </>
                          )}
                          {org.deletedAt && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={(e) => handleRestore(org, e)}>
                                <RotateCcw className="h-4 w-4 mr-2" />
                                Restore Organization
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <div className="text-sm text-muted-foreground">
                {data.totalPages > 1
                  ? <>Showing {(data.page - 1) * data.limit + 1} to{' '}
                    {Math.min(data.page * data.limit, data.total)} of {data.total} organizations</>
                  : <>Showing {data.total} {data.total === 1 ? 'organization' : 'organizations'}</>
                }
              </div>
              {data.totalPages > 1 && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(data.page - 1)}
                    disabled={data.page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(data.page + 1)}
                    disabled={data.page === data.totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        variant={confirmDialog.variant}
        loading={updateSubscription.isPending || restoreOrganization.isPending}
      />

      <CreateOrganizationDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleCreateOrganization}
        loading={createOrganization.isPending}
      />
    </div>
  );
}
